// Admin → Reports. Revenue, refunds, MRR/ARR and churn, plus the CSV exports.
// M7c / P5. English chrome (staff tool).
//
// 🔴 EVERY FIGURE IS PER CURRENCY, shown side by side and never summed.
// We sell in seven currencies and hold no FX table, so a single headline
// "revenue" number would be fiction — and worse, fiction that looks
// authoritative enough to act on. When a real rate source exists, a blended
// view becomes its own decision.
//
// Charts are hand-rolled inline SVG (components/Chart.tsx) rather than a
// charting dependency: ~500KB against a <250KB budget, and a new third-party
// dep is ask-first per CLAUDE.md §7.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  BarChart,
  DateRangePicker,
  DonutChart,
  ErrorState,
  Icon,
  LineChart,
  Money,
  PageHeader,
  StatGrid,
  StatTile,
  resolveDateRange,
  type DateRange,
} from '~/components';
import { downloadReport } from '~/lib/admin';
import { formatMinorAmount } from '~/lib/money';
import { getRevenueReport, getSubscriptionReport } from '~/lib/reports';

export const Route = createFileRoute('/_authed/reports')({
  component: ReportsPage,
});

function ReportsPage() {
  const [range, setRange] = useState<DateRange>(() => resolveDateRange('90d'));

  const revenue = useQuery({
    queryKey: ['reports', 'revenue', range.from, range.to],
    queryFn: ({ signal }) => getRevenueReport(range, signal),
  });

  const subs = useQuery({
    queryKey: ['reports', 'subscriptions', range.from, range.to],
    queryFn: ({ signal }) => getSubscriptionReport(range, signal),
  });

  const totals = revenue.data?.totals ?? [];
  const s = subs.data;

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Reports"
        description="Revenue, refunds and subscription health. Every figure is per currency — never blended."
        actions={<DateRangePicker value={range} onChange={setRange} />}
      />

      {revenue.error && (
        <ErrorState
          error={revenue.error}
          onRetry={() => void revenue.refetch()}
          className="rounded-xl border border-admin-200 bg-white"
        />
      )}

      {!revenue.error && (
        <>
          <StatGrid className="mb-6">
            <StatTile
              label="Collected"
              icon="money"
              loading={revenue.isLoading}
              value={
                <CurrencyStack
                  rows={totals.map((t) => ({
                    currency: t.currency,
                    amountMinor: t.collectedMinor,
                  }))}
                />
              }
              hint="gross, in the window"
            />
            <StatTile
              label="Refunded"
              icon="undo"
              loading={revenue.isLoading}
              value={
                <CurrencyStack
                  rows={totals.map((t) => ({ currency: t.currency, amountMinor: t.refundedMinor }))}
                  hideZero
                />
              }
              hint="succeeded refunds"
            />
            <StatTile
              label="MRR"
              icon="chart"
              loading={subs.isLoading}
              value={<CurrencyStack rows={toRows(s?.mrrByCurrency)} />}
              hint="monthly-normalised, paid plans only"
            />
            <StatTile
              label="Churn"
              icon="history"
              loading={subs.isLoading}
              value={s?.churnRate == null ? '—' : `${(s.churnRate * 100).toFixed(1)}%`}
              hint={s ? `${s.churnedInWindow} canceled · ${s.newInWindow} new` : undefined}
            />
          </StatGrid>

          {/* One chart per currency. Overlaying them on shared axes would imply
              the values are comparable, which across currencies they are not. */}
          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            {revenue.isLoading && (
              <div className="h-64 animate-pulse rounded-xl border border-admin-200 bg-white" />
            )}
            {(revenue.data?.series ?? []).map((series) => (
              <LineChart
                key={series.currency}
                title={`${series.currency} collected per day`}
                height={200}
                data={series.points.map((p) => ({
                  label: p.day.slice(5),
                  value: p.collectedMinor,
                }))}
                valueFormatter={(v) => formatMinorAmount(v, series.currency)}
              />
            ))}
          </div>

          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            <DonutChart
              title="Active subscriptions by plan"
              data={Object.entries(s?.byPlan ?? {}).map(([label, value]) => ({ label, value }))}
            />
            <BarChart
              title="Subscriptions by status"
              data={Object.entries(s?.byStatus ?? {}).map(([label, value]) => ({ label, value }))}
            />
          </div>

          <div className="mb-6 overflow-hidden rounded-xl border border-admin-200 bg-white">
            <div className="border-b border-admin-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-admin-800">Per-currency breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-admin-200 bg-admin-50 text-[11px] uppercase tracking-wider text-admin-500">
                    <th className="px-4 py-2.5 text-start">Currency</th>
                    <th className="px-4 py-2.5 text-end">Invoices</th>
                    <th className="px-4 py-2.5 text-end">Collected</th>
                    <th className="px-4 py-2.5 text-end">Refunded</th>
                    <th className="px-4 py-2.5 text-end">Discounts</th>
                    <th className="px-4 py-2.5 text-end">Net</th>
                    <th className="px-4 py-2.5 text-end">MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-admin-400">
                        No revenue in this period.
                      </td>
                    </tr>
                  ) : (
                    totals.map((t) => (
                      <tr key={t.currency} className="border-b border-admin-100 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-admin-900">{t.currency}</td>
                        <td className="px-4 py-2.5 text-end tabular-nums text-admin-600">
                          {t.invoiceCount}
                        </td>
                        <td className="px-4 py-2.5 text-end">
                          <Money
                            amountMinor={t.collectedMinor}
                            currency={t.currency}
                            showCurrency={false}
                          />
                        </td>
                        <td className="px-4 py-2.5 text-end">
                          {t.refundedMinor > 0 ? (
                            <Money
                              amountMinor={-t.refundedMinor}
                              currency={t.currency}
                              showCurrency={false}
                              signed
                            />
                          ) : (
                            <span className="text-admin-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-end">
                          {t.discountMinor > 0 ? (
                            <Money
                              amountMinor={-t.discountMinor}
                              currency={t.currency}
                              showCurrency={false}
                              signed
                            />
                          ) : (
                            <span className="text-admin-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-end">
                          <Money
                            amountMinor={t.netMinor}
                            currency={t.currency}
                            showCurrency={false}
                            emphasis="strong"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-end">
                          {s?.mrrByCurrency[t.currency] ? (
                            <Money
                              amountMinor={s.mrrByCurrency[t.currency]!}
                              currency={t.currency}
                              showCurrency={false}
                            />
                          ) : (
                            <span className="text-admin-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="border-t border-admin-100 bg-admin-50 px-4 py-2 text-xs text-admin-400">
              Rows are never summed: there is no exchange-rate source, so a combined total would be
              a guess.
            </p>
          </div>
        </>
      )}

      <CsvExports />
    </div>
  );
}

function CurrencyStack({
  rows,
  hideZero = false,
}: {
  rows: Array<{ currency: string; amountMinor: number }>;
  hideZero?: boolean;
}) {
  const visible = hideZero ? rows.filter((r) => r.amountMinor !== 0) : rows;
  if (visible.length === 0) return <span className="text-admin-400">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {visible.map((r) => (
        <Money
          key={r.currency}
          amountMinor={r.amountMinor}
          currency={r.currency}
          emphasis="strong"
        />
      ))}
    </div>
  );
}

function toRows(byCurrency: Record<string, number> | undefined) {
  if (!byCurrency) return [];
  return Object.entries(byCurrency).map(([currency, amountMinor]) => ({ currency, amountMinor }));
}

const CSV_REPORTS: Array<{
  kind: 'users' | 'revenue' | 'engagement';
  title: string;
  desc: string;
}> = [
  { kind: 'users', title: 'Users', desc: 'Every account — kind, status, roles, join date.' },
  { kind: 'revenue', title: 'Revenue', desc: 'Paid invoices — owner, plan, amount, paid date.' },
  { kind: 'engagement', title: 'Engagement', desc: 'Per-learner lessons completed + XP.' },
];

function CsvExports() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function download(kind: 'users' | 'revenue' | 'engagement') {
    setBusy(kind);
    setError(null);
    try {
      await downloadReport(kind);
    } catch {
      setError('Download failed — try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-xl border border-admin-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-admin-800">CSV exports</h2>
      <p className="mt-0.5 text-xs text-admin-500">Full datasets for spreadsheets.</p>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700">
          {error}
        </p>
      )}

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {CSV_REPORTS.map((r) => (
          <button
            key={r.kind}
            type="button"
            onClick={() => void download(r.kind)}
            disabled={busy != null}
            className="flex items-start gap-2.5 rounded-lg border border-admin-200 p-3 text-start transition-colors hover:bg-admin-50 disabled:opacity-50"
          >
            <span className="mt-0.5 text-admin-400">
              <Icon name="download" className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-admin-800">
                {busy === r.kind ? 'Preparing…' : r.title}
              </span>
              <span className="mt-0.5 block text-xs text-admin-500">{r.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
